import styled, { css } from 'styled-components';
import tw, { theme } from 'twin.macro';
import PulsingDot from '../../../components/PulsingDot/PulsingDot';
import type { RowDisplayStatus } from '../../listViewRowStatus';
import type { BookingRowStatus } from '../../types';

/* What the two view modes share. Both mark their rows the same way — see
 * listViewRowStatus for what a status means — so the tints they are marked
 * with live here once, and the card and the table each compose them into the
 * element they render. Everything a single view mode draws lives with the
 * component that draws it. */

/* 'changed' is not in here: it tints the changed cells rather than the whole
   row. Neither is 'past', which greys the row instead of tinting it, nor
   "running right now", which is a pulsing dot in front of the booking's name
   and no tint at all - see listViewRowStatus. */
const rowTint = {
  deleted: tw`bg-red-lighten-30`,
  new: tw`bg-green-lighten-30`
};

/* The same tints one shade lighter, for the row of a booking that is a member
   of a set: those rows already sit on a shaded band under the row of the set
   they belong to, and the full tint there reads as a second block of colour
   rather than as the same mark one level in. */
const setMemberRowTint = {
  deleted: tw`bg-red-lighten-40`,
  new: tw`bg-green-lighten-40`
};

const isTinted = (
  $status: RowDisplayStatus | BookingRowStatus | undefined
): $status is keyof typeof rowTint => $status === 'deleted' || $status === 'new';

export const statusBackground = (
  $status: RowDisplayStatus | BookingRowStatus | undefined,
  $isSetMember: boolean
) => [isTinted($status) && ($isSetMember ? setMemberRowTint : rowTint)[$status]];

export const deletedText = tw`line-through text-red-darken-10`;
export const changedTint = tw`bg-orange-lighten-30`;

/* A booking that has ended: greyed rather than tinted, and kept readable at
   that. Both view modes grey the whole element they render the booking in -
   the card, and the row of the table - so the two fade by the same amount. */
export const pastRow = tw`opacity-60`;

/* The rule that ties the members of a set to the set they belong to: down the
   left of the members inside a card, and down the left of the name cell of a
   member's row in the table */
export const setMemberRule = css`
  border-left: 2px solid ${theme`colors.silver.lighten-10`};
`;

/* The mark on a booking that is running right now, in front of its value: the
   shared PulsingDot, spaced and centred for the line it sits on. Both view
   modes use it, so the two agree on where the mark sits and what it says. */
export const HappeningNowDot = styled(PulsingDot)(tw`self-center mr-6`);

/* The room that mark takes in front of a value: the 8px dot PulsingDot draws
   plus the 6px it keeps from the value beside it. Everything in the column
   the dot marks starts after this much - the rows without a dot hold it open
   (HappeningNowDotPlaceholder) and the column's header is indented by it -
   so the header, a running booking and one that is not all line up. */
export const happeningNowDotRoom = '0.875rem'; /* 14px: the dot's 8px plus its 6px */
